import axios from "axios";
import { notification } from "antd";
import { API_BASE_URL } from "configs/AppConfig";
import { AUTH_TOKEN, REFRESH_TOKEN, USER_INFO } from "constants/AuthConstant";
import store from "../store";
import { signOutSuccess, updateTokens } from "store/slices/authSlice";
import { resetWsAuth, closeWS } from "../wsClient";

/**
 * ApiCallInstance
 * ----------------
 * Single, robust axios instance used for EVERY endpoint in the app.
 *
 * Features:
 *  - Injects the Bearer access token on every request (unless `skipAuth: true`)
 *  - Transparently refreshes an expired access token using the refresh token
 *  - Queues concurrent requests while a refresh is in-flight (only ONE refresh
 *    call is ever made, avoiding refresh-token rotation races that used to
 *    cause random logouts)
 *  - Rotates + persists the new tokens, keeps Redux + WebSocket auth in sync
 *  - Only logs the user out when the refresh itself genuinely fails
 *
 * NOTE: The default axios behaviour is preserved (resolves with the full
 * response object, so callers use `res.data`). A thin `FetchInterceptor`
 * wrapper is provided for legacy code that expects the unwrapped payload.
 */

const apiCall = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

// ---------------------------------------------------------------------------
// Refresh coordination state
// ---------------------------------------------------------------------------
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

const getStoredToken = () =>
  typeof window !== "undefined" ? localStorage.getItem(AUTH_TOKEN) : null;

const clearSession = () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  localStorage.removeItem(USER_INFO);
};

const forceLogout = (silent = false) => {
  clearSession();
  closeWS();
  try {
    store.dispatch(signOutSuccess());
  } catch (e) {
    /* store not ready — ignore */
  }
  if (!silent) {
    notification.error({
      message: "Session Expired",
      description: "Your session has ended. Please sign in again.",
    });
  }
};

/**
 * Perform the actual refresh call. Uses a bare axios instance so it never
 * re-enters this interceptor (which would cause an infinite loop).
 */
const refreshAccessToken = async () => {
  const refreshToken =
    typeof window !== "undefined" ? localStorage.getItem(REFRESH_TOKEN) : null;

  if (!refreshToken) {
    throw new Error("NO_REFRESH_TOKEN");
  }

  const { data } = await axios.post(
    `${API_BASE_URL}/auth/refresh`,
    { refreshToken },
    { timeout: 30000 }
  );

  const {
    accessToken,
    token,
    refreshToken: newRefreshToken,
    user,
  } = data || {};

  const nextAccess = accessToken || token;
  if (!nextAccess) {
    throw new Error("NO_ACCESS_TOKEN_IN_REFRESH");
  }

  localStorage.setItem(AUTH_TOKEN, nextAccess);
  if (newRefreshToken) {
    localStorage.setItem(REFRESH_TOKEN, newRefreshToken);
  }
  if (user) {
    localStorage.setItem(USER_INFO, JSON.stringify(user));
  }

  // Keep Redux + WebSocket auth in sync with the freshly minted tokens.
  try {
    store.dispatch(
      updateTokens({ token: nextAccess, refreshToken: newRefreshToken })
    );
  } catch (e) {
    /* ignore */
  }
  resetWsAuth();

  return nextAccess;
};

// ---------------------------------------------------------------------------
// Request interceptor — inject Authorization header
// ---------------------------------------------------------------------------
apiCall.interceptors.request.use(
  (config) => {
    if (config.skipAuth) {
      return config;
    }
    const token = getStoredToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------------
// Response interceptor — transparent refresh + error handling
// ---------------------------------------------------------------------------
const isAuthEndpoint = (url = "") =>
  url.includes("/auth/refresh") ||
  url.includes("/auth/login") ||
  url.includes("/auth/register");

apiCall.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const hasRefreshToken =
      typeof window !== "undefined" && !!localStorage.getItem(REFRESH_TOKEN);

    // -- 401: try a transparent refresh (once) ------------------------------
    if (
      status === 401 &&
      !originalRequest._retry &&
      !originalRequest.skipAuth &&
      !isAuthEndpoint(originalRequest.url) &&
      hasRefreshToken
    ) {
      // A refresh is already happening — queue this request until it resolves.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiCall(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        processQueue(null, newToken);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiCall(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // -- 401 without a usable refresh token — hard logout -------------------
    if (status === 401 && !isAuthEndpoint(originalRequest.url)) {
      forceLogout();
      return Promise.reject(error);
    }

    // -- 403 forbidden ------------------------------------------------------
    if (status === 403) {
      notification.error({
        message: "Access Denied",
        description:
          error.response?.data?.message ||
          "You do not have permission to perform this action.",
      });
      return Promise.reject(error);
    }

    // -- Generic error notifications ---------------------------------------
    if (!axios.isCancel(error)) {
      const notificationParam = {
        message: "Error",
        description:
          error.response?.data?.message || error.message || "Request failed",
      };
      if (status === 404) notificationParam.message = "Not Found";
      if (status === 500) notificationParam.message = "Internal Server Error";
      if (status === 508) notificationParam.message = "Request Timeout";
      notification.error(notificationParam);
    }

    return Promise.reject(error);
  }
);

export default apiCall;
